'use strict'

import React from 'react'
import classnames from 'classnames'

import SwitchOption from './SwitchOption'

import './Switch.less'

/**
 * A constant, default class for every Switch instance
 * @constant
 * @private
 */
const COMPONENT_CLASS = 'nmc-switch'

/**
 * A constant, default extra classes for every Switch instance
 * @constant
 * @private
 */
const COMPONENT_ALIASES = ['nmc-switch-container']

/**
 * Return true if two arrays are equal (not deep equal!)
 * @param {Array} arr1 - first array for comparison
 * @param {Array} arr2 - second array for comparison
 * @returns {Boolean}
 */
const isArrayEqual = (arr1, arr2) => {
  if (arr1.length !== arr2.length) {
    return false
  }
  return arr1.every((item, index) => {
    return item === arr2[index]
  })
}

/**
 * @class Switch
 */
export default class Switch extends React.Component {
  /**
   * @constructor
   * @param {Object} props - properties for component
   * @param {Boolean} disabled - if the component is disabled
   * @param {Boolean} allow-empty - allows to not uncheck every option
   * @param {Boolean} multi - allows multi selection
   * @param {Number} limit - a limit to the number of selections
   * @param {String} default-selection - data-id of the default selection
   * @param {Function} onSelect - a callback wich will be called when there's a new selection
   * @param {Array} selected - a selected array. using it will make the component controlled from the outside without it's inner logic
   */
  constructor (props) {
    super(props)
    /**
     * takes the default selection as an array
     * if the selection is multi-select then takes only the first child
     */
    const selection = this.props.multi
      ? this.getDefaultSelection(props)
      : [this.getDefaultSelection(props)[0]]
    this.state = {selection}
    this.onSelect = this.onSelect.bind(this)
  }
  componentWillReceiveProps (props) {
    const selection = props.multi
      ? this.getDefaultSelection(props)
      : [this.getDefaultSelection(props)[0]]
    this.setState({selection})
    this.onSelect = this.onSelect.bind(this)
  }

  /**
   * Returns the default selection of the component
   * @returns {Array<String>|undefined}
   */
  getDefaultSelection (props) {
    const selected = props.selected || props['default-selection']
    if (!selected) {
      return []
    } else if (Array.isArray(selected)) {
      return selected
    } else {
      return selected.split(',').map(item => item.trim())
    }
  }

  /**
   * Returns true if the component is controlled from outside
   * @returns {Boolean}
   */
  isControlled () {
    return this.props.hasOwnProperty('selected')
  }

  /**
   * Returns true if the component is disabled
   */
  isDisabled () {
    return this.props.hasOwnProperty('disabled') && this.props['disabled'] !== false
  }

  /**
   * Returns true if the selection is suppose to be for multi-selection:
   * If the attribute 'multi' exist and contains any value but 'false'.
   * @returns {Boolean}
   */
  isMulti () {
    return this.props.hasOwnProperty('multi') && this.props.multi !== false
  }

  /**
   * Returns true if the selection is suppose to allow null values (meaning no selection is valid)
   */
  isAllowingEmpty () {
    return this.props.hasOwnProperty('allow-empty') && this.props['allow-empty'] !== false
  }

  /**
   * Returns true if there's a selections number limit
   * @returns {Boolean}
   */
  isLimited () {
    return this.props.hasOwnProperty('limit')
  }

  /**
   * Return the limit of selections
   * @default 1
   * @returns {Number}
   */
  getLimit () {
    return this.props['limit'] ? Number.parseInt(this.props['limit'], 10) : 1
  }

  /**
   * Selects an option or multiple options
   * Each new id that is being selected (assuming it's a multi-selection) is being added as the first node!
   * @param {String} id - the id of the option to select
   * @param {Function} callback - a callback to use for after the event is done (usually would be called from onSelect)
   */
  select (id, callback) {
    // copies the selection array
    const selected = Array.prototype.slice.call(this.state.selection)
    // if the current id is already selected
    if (this.isSelected(id)) {
      if (this.isMulti() || this.isAllowingEmpty()) {
        selected.splice(selected.indexOf(id), 1)
      }
    } else {
      if (this.isMulti() || selected.length === 0) {
        // if the selections passes the limit - remove the last that was added
        if (this.isLimited() && this.getLimit() <= selected.length) {
          selected.splice(selected.length - 1, 1)
        }
        // in any case - push the current id as the latest one
        selected.unshift(id)
      } else {
        // removes everything and adds only the current id
        selected.splice(0, selected.length, id)
      }
    }

    // if there is callback and there was a change - call it asyncly
    if (typeof callback === 'function' && !isArrayEqual(selected, this.state.selection)) {
      const arg = this.isMulti() ? selected : selected[0]
      this.setState({selection: selected}, () => callback(arg))
    } else {
      this.setState({selection: selected})
    }
  }

  /**
   * Returns true if the id is selected
   * Use the selected property if it exist or the selection state with the inner logic
   * @param {*} id - an id (data-id value)
   * @returns {Boolean}
   */
  isSelected (id) {
    return this.state.selection.includes(id)
  }

  /**
   * On select event handler
   * Set to interface between the event data and the component infrastructure
   * If the component is controlled - use the onSelect as onClick and call the event with the current clicked id
   */
  onSelect (id) {
    if (this.isControlled()) {
      return this.props.onSelect(id)
    } else if (!this.isDisabled()) {
      return this.select(id, this.props.onSelect ? this.props.onSelect : null)
    }
  }

  render () {
    const classNames = classnames(COMPONENT_CLASS, COMPONENT_ALIASES, {
      'is-disabled': this.props.disabled,
      'allow-empty': this.props['allow-empty'],
      'multi': this.props.multi
    })
    return (
      <div className={classNames}>
        {
          React.Children.map(this.props.children, (element, index) => {
            const dataId = element.props['data-id'] || String(index)
            return <SwitchOption
              isSelected={this.isSelected(dataId)}
              data-id={dataId}
              key={index}
              onSelect={this.onSelect}
              {...element.props}
            >{element.props.children}
            </SwitchOption>
          })
       }
      </div>
    )
  }
}

Switch.COMPONENT_CLASS = COMPONENT_CLASS
Switch.COMPONENT_ALIASES = COMPONENT_ALIASES

Switch.SwitchOption = SwitchOption



// WEBPACK FOOTER //
// ../~/@master/webinfra-frontend-components/src/components/Switch/index.js